import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuthModal } from '../context/AuthModalContext';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const whatWeDo = [
  'Create engaging animated educational content for children.',
  'Deliver bite-sized daily learning through WhatsApp.',
  'Promote African languages and culture using modern technology.',
  'Develop educational content that is simple, fun, and easy to access.',
  'Build innovative digital learning solutions for families, schools, and communities.',
];

const values = [
  {
    title: 'Innovation',
    description: 'We constantly seek better ways to educate through technology and creativity.',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  },
  {
    title: 'Excellence',
    description: 'We strive to produce world-class educational content that inspires confidence and curiosity.',
    icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.539 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.784.57-1.838-.196-1.539-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.63 9.101c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    fill: true,
  },
  {
    title: 'Accessibility',
    description: 'Quality education should be available to every child, wherever they are.',
    icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
  },
  {
    title: 'Cultural Pride',
    description: "We celebrate Africa's languages, heritage, and identity by making native language learning enjoyable.",
    icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    title: 'Integrity',
    description: 'We are committed to delivering trusted, safe, and meaningful educational experiences.',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  },
];

const SectionEyebrow = ({ children }) => (
  <span className="inline-block bg-indigo-100 text-indigo-800 text-sm font-semibold px-4 py-1 rounded-full mb-4">
    {children}
  </span>
);

const About = () => {
  const { openModal } = useAuthModal();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Intro band */}
        <section className="relative bg-gradient-to-br from-indigo-900 to-purple-800 text-white py-20 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-indigo-500 filter blur-3xl"></div>
            <div className="absolute bottom-10 right-20 w-60 h-60 rounded-full bg-purple-600 filter blur-3xl"></div>
          </div>
          <motion.div
            className="relative container mx-auto px-6 lg:px-8 text-center max-w-3xl"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Welcome to Eunbro Ltd.</h1>
            <p className="text-xl md:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-300 font-semibold">
              Inspiring the Next Generation Through Innovation
            </p>
          </motion.div>
        </section>

        {/* Mission intro */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
            >
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                At Eunbro Ltd., we believe that every child deserves to enjoy learning. Our
                mission is to transform education into an exciting, engaging, and memorable
                experience by combining technology, creativity, and animation.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We develop innovative digital learning experiences that children can enjoy on the
                devices they already use every day&mdash;smartphones, tablets, and laptops. As
                long as you have WhatsApp, learning is only a message away.
              </p>
            </motion.div>
          </div>
        </section>

        {/* African languages */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
            >
              <SectionEyebrow>Our Flagship Experience</SectionEyebrow>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Making African Languages <span className="text-indigo-600">Fun Again</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our flagship learning experience delivers an African Language Word of the Day
                through short, high-quality animated video clips. Each lesson is designed to
                capture a child's imagination while naturally building vocabulary,
                pronunciation, confidence, and a love for their native language.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Children already love cartoons. We simply bring learning into the world they
                enjoy most&mdash;turning every lesson into an adventure they look forward to.
              </p>
            </motion.div>
          </div>
        </section>

        {/* What we do */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
            >
              <SectionEyebrow>What We Do</SectionEyebrow>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Turning Learning Into an <span className="text-indigo-600">Adventure</span>
              </h2>
            </motion.div>

            <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {whatWeDo.map((item, index) => (
                <motion.div
                  key={item}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeUp}
                  transition={{ delay: index * 0.08 }}
                  className="flex items-start"
                >
                  <svg
                    className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-indigo-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-700">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our values */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
            >
              <SectionEyebrow>What We Stand For</SectionEyebrow>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Our Values</h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeUp}
                  transition={{ delay: (index % 3) * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl hover:border-indigo-100 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-5">
                    <svg
                      className="w-6 h-6 text-indigo-600"
                      fill={value.fill ? 'currentColor' : 'none'}
                      stroke={value.fill ? 'none' : 'currentColor'}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d={value.icon}
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeUp}
                className="bg-gradient-to-br from-indigo-600 to-purple-600 text-white p-10 rounded-2xl shadow-lg"
              >
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-5">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-indigo-100 leading-relaxed">
                  To become Africa's leading digital educational entertainment company,
                  empowering millions of children to learn, grow, and connect with their
                  cultural heritage through innovative technology and storytelling.
                </p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeUp}
                transition={{ delay: 0.1 }}
                className="bg-gray-900 text-white p-10 rounded-2xl shadow-lg"
              >
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-5">
                  <svg className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 3v18h2v-8h13l-3-4 3-4H5V3H3z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-gray-300 leading-relaxed">
                  To make learning fun, accessible, and inspiring by creating engaging animated
                  educational experiences that nurture curiosity, strengthen language skills, and
                  prepare children for a brighter future.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="relative bg-gradient-to-br from-indigo-900 to-purple-800 text-white py-20 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-10 right-20 w-60 h-60 rounded-full bg-purple-600 filter blur-3xl"></div>
            <div className="absolute bottom-10 left-20 w-40 h-40 rounded-full bg-indigo-500 filter blur-3xl"></div>
          </div>
          <motion.div
            className="relative container mx-auto px-6 lg:px-8 text-center max-w-2xl"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Join the Learning Adventure</h2>
            <p className="text-lg text-gray-200 leading-relaxed mb-10">
              At Eunbro, we are not just teaching words&mdash;we are helping children discover
              their identity, embrace their culture, and build confidence one animated lesson at
              a time.
            </p>
            <button
              type="button"
              onClick={() => openModal('signup')}
              className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-yellow-300 text-gray-900 px-8 py-4 rounded-xl font-bold hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 mb-12"
            >
              Get Started
            </button>
            <div className="pt-10 border-t border-white/10">
              <p className="text-2xl font-bold">Eunbro Ltd.</p>
              <p className="text-yellow-300 font-medium mt-1">Learn. Laugh. Grow.</p>
            </div>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
